import Phaser from '../../../lib/phaser.js';
import { UI_ASSET_KEYS } from '../../../assets/asset-keys.js';
import { DIRECTION } from '../../../common/direction.js';
import { exhaustiveGuard } from '../../../utils/guard.js';
import { ACTIVE_BATTLE_MENU, ATTACK_MOVE_OPTIONS, BATTLE_MENU_OPTIONS } from './battle-menu-options.js';
import { BATTLE_UI_TEXT_STYLE } from './battle-menu-config.js';
import { KENNEY_FUTURE_NARROW_FONT_NAME } from '../../../assets/font-keys.js';
import { BattleMonster } from '../../monsters/battle-monster.js';
import { animateText } from '../../../utils/text-utils.js';
import { dataManager } from '../../../utils/data-manager.js';
import { SCENE_KEYS } from '../../../scenes/scene-keys.js';
import { ITEM_CATEGORY } from '../../../types/typedef.js';

// The battle UI is authored against this fixed design resolution (the forest battle background is
// exactly this size). The BattleScene camera scales this design space to cover the real viewport,
// so all menu/pane coordinates here MUST be expressed in design space (not this.scale.width/height,
// which is the actual — much larger — window size under Phaser.Scale.RESIZE).
const BATTLE_DESIGN_WIDTH = 1024;
const BATTLE_DESIGN_HEIGHT = 576;

const BATTLE_MENU_CURSOR_POS = Object.freeze({
  x: 42,
  y: 38,
});

const ATTACK_MENU_CURSOR_POS = Object.freeze({
  x: 42,
  y: 38,
});

const PLAYER_INPUT_CURSOR_POS = Object.freeze({
  y: 488,
});

export class BattleMenu {
  /** @type {Phaser.Scene} */
  #scene;
  /** @type {Phaser.GameObjects.Container} */
  #mainBattleMenuPhaserContainerGameObject;
  /** @type {Phaser.GameObjects.Container} */
  #moveSelectionSubBattleMenuPhaserContainerGameObject;
  /** @type {Phaser.GameObjects.Text} */
  #battleTextGameObjectLine1;
  /** @type {Phaser.GameObjects.Text} */
  #battleTextGameObjectLine2;
  /** @type {Phaser.GameObjects.Image} */
  #mainBattleMenuCursorPhaserImageGameObject;
  /** @type {Phaser.GameObjects.Image} */
  #attackBattleMenuCursorPhaserImageGameObject;
  /** @type {import('./battle-menu-options.js').BattleMenuOptions} */
  #selectedBattleMenuOption;
  /** @type {import('./battle-menu-options.js').AttackMoveOptions} */
  #selectedAttackMenuOption;
  /** @type {import('./battle-menu-options.js').ActiveBattleMenu} */
  #activeBattleMenu;
  /** @type {{ messages: string[]; callback: () => void | undefined; isProcessing: boolean; }[]} */
  #infoPanelMessagesQueue;
  /** @type {boolean} */
  #waitingForPlayerInput;
  /** @type {number | undefined} */
  #selectedAttackIndex;
  /** @type {BattleMonster} */
  #activePlayerMonster;
  /** @type {Phaser.GameObjects.Image} */
  #userInputCursorPhaserImageGameObject;
  /** @type {Phaser.Tweens.Tween} */
  #userInputCursorPhaserTween;
  /** @type {boolean} */
  #skipAnimations;
  /** @type {boolean} */
  #queuedMessageAnimationPlaying;
  /** @type {{ skip: () => void, done: boolean } | undefined} active queued-message typewriter */
  #queuedTextAnim;
  /** @type {import('../../../types/typedef.js').Item | undefined} */
  #usedItem;
  /** @type {boolean} */
  #fleeAttempt;
  /** @type {boolean} */
  #switchMonsterAttempt;
  /** @type {boolean} */
  #wasItemUsed;
  /** @type {boolean} */
  #isTrainerBattle;
  /** @type {(() => void) | undefined} mouse/touch confirm callback wired by the scene */
  #onPointerConfirm;
  /** @type {Phaser.GameObjects.Text} blinking "press to continue" hint shown while a message gate waits */
  #continueHintGameObject;
  /** @type {Phaser.Tweens.Tween} */
  #continueHintTween;

  /**
   *
   * @param {Phaser.Scene} scene the Phaser 3 Scene the battle menu will be added to
   * @param {BattleMonster} activePlayerMonster the players current active monster in the current battle
   * @param {boolean} [skipBattleAnimations=false] used to skip all animations tied to the battle
   * @param {boolean} [isTrainerBattle=false] used to determine if the battle is against a trainer
   */
  constructor(scene, activePlayerMonster, skipBattleAnimations = false, isTrainerBattle = false) {
    this.#scene = scene;
    this.#activePlayerMonster = activePlayerMonster;
    this.#activeBattleMenu = ACTIVE_BATTLE_MENU.BATTLE_MAIN;
    this.#selectedBattleMenuOption = BATTLE_MENU_OPTIONS.FIGHT;
    this.#selectedAttackMenuOption = ATTACK_MOVE_OPTIONS.MOVE_1;
    this.#infoPanelMessagesQueue = [];
    this.#waitingForPlayerInput = false;
    this.#selectedAttackIndex = undefined;
    this.#skipAnimations = skipBattleAnimations;
    this.#queuedMessageAnimationPlaying = false;
    this.#isTrainerBattle = isTrainerBattle;
    this.#wasItemUsed = false;
    this.#usedItem = undefined;
    this.#fleeAttempt = false;
    this.#switchMonsterAttempt = false;
    this.#createMainInfoPane();
    this.#createMainBattleMenu();
    this.#createMonsterAttackSubMenu();
    this.#createPlayerInputCursor();

    // Conditionally disable FLEE button
    if (this.#isTrainerBattle) {
      /** @type {Phaser.GameObjects.Text} */ (this.#mainBattleMenuPhaserContainerGameObject.getAt(4)).setAlpha(0.5);
    }

    this.#scene.events.on(Phaser.Scenes.Events.RESUME, this.#handleSceneResume, this);
    this.#scene.events.once(
      Phaser.Scenes.Events.SHUTDOWN,
      () => {
        this.#scene.events.off(Phaser.Scenes.Events.RESUME, this.#handleSceneResume, this);
      },
      this
    );
  }

  /**
   * Wire mouse/touch input so the battle menu is fully clickable (keyboard still works). Clicking
   * a main option (Fight / Switch / Item / Flee) or an attack selects it and confirms; clicking the
   * info pane while waiting advances queued messages — mirroring the SPACE key. The scene passes a
   * confirm callback that runs the same post-select state transitions as a keyboard confirm.
   * @param {() => void} onConfirm invoked after a player select/confirm so the scene can advance state
   * @returns {void}
   */
  enablePointerInput(onConfirm) {
    this.#onPointerConfirm = onConfirm;

    /**
     * @param {Phaser.GameObjects.Text} buttonText
     * @param {import('./battle-menu-options.js').BattleMenuOptions} option
     */
    const wireMainButton = (buttonText, option) => {
      buttonText.setInteractive({ useHandCursor: true });
      buttonText.on(Phaser.Input.Events.POINTER_DOWN, (/** @type {Phaser.Input.Pointer} */ p, _x, _y, event) => {
        event?.stopPropagation?.();
        this.#handleMainOptionClicked(option);
      });
    };
    wireMainButton(
      /** @type {Phaser.GameObjects.Text} */ (this.#mainBattleMenuPhaserContainerGameObject.getAt(1)),
      BATTLE_MENU_OPTIONS.FIGHT
    );
    wireMainButton(
      /** @type {Phaser.GameObjects.Text} */ (this.#mainBattleMenuPhaserContainerGameObject.getAt(2)),
      BATTLE_MENU_OPTIONS.SWITCH
    );
    wireMainButton(
      /** @type {Phaser.GameObjects.Text} */ (this.#mainBattleMenuPhaserContainerGameObject.getAt(3)),
      BATTLE_MENU_OPTIONS.ITEM
    );
    wireMainButton(
      /** @type {Phaser.GameObjects.Text} */ (this.#mainBattleMenuPhaserContainerGameObject.getAt(4)),
      BATTLE_MENU_OPTIONS.FLEE
    );

    const attackOptions = [
      ATTACK_MOVE_OPTIONS.MOVE_1,
      ATTACK_MOVE_OPTIONS.MOVE_2,
      ATTACK_MOVE_OPTIONS.MOVE_3,
      ATTACK_MOVE_OPTIONS.MOVE_4,
    ];
    attackOptions.forEach((option, index) => {
      const buttonText = /** @type {Phaser.GameObjects.Text} */ (
        this.#moveSelectionSubBattleMenuPhaserContainerGameObject.getAt(index)
      );
      buttonText.setInteractive({ useHandCursor: true });
      buttonText.on(Phaser.Input.Events.POINTER_DOWN, (/** @type {Phaser.Input.Pointer} */ p, _x, _y, event) => {
        event?.stopPropagation?.();
        this.#handleAttackOptionClicked(option);
      });
    });
  }

  /**
   * Click handler for a main menu option: only acts while the main menu is showing; selects the
   * option (moving the cursor) and confirms it.
   * @param {import('./battle-menu-options.js').BattleMenuOptions} option
   * @returns {void}
   */
  #handleMainOptionClicked(option) {
    // A tap while a message gate is up advances the gate (instead of being silently dropped) — so a
    // touch player is never stuck unable to clear "press to continue" with no keyboard.
    if (this.#waitingForPlayerInput) {
      this.handlePlayerInput('OK');
      this.#onPointerConfirm?.();
      return;
    }
    if (this.#activeBattleMenu !== ACTIVE_BATTLE_MENU.BATTLE_MAIN) {
      return;
    }
    // FLEE is disabled in trainer battles
    if (option === BATTLE_MENU_OPTIONS.FLEE && this.#isTrainerBattle) {
      return;
    }
    this.#selectedBattleMenuOption = option;
    this.#moveMainBattleMenuCursor();
    this.#handlePlayerChooseMainBattleOption();
    this.#onPointerConfirm?.();
  }

  /**
   * Click handler for an attack option: only acts while the attack sub-menu is showing; selects the
   * move and confirms it.
   * @param {import('./battle-menu-options.js').AttackMoveOptions} option
   * @returns {void}
   */
  #handleAttackOptionClicked(option) {
    // A tap while a message gate is up advances the gate (never dropped) — touch players have no SPACE.
    if (this.#waitingForPlayerInput) {
      this.handlePlayerInput('OK');
      this.#onPointerConfirm?.();
      return;
    }
    if (this.#activeBattleMenu !== ACTIVE_BATTLE_MENU.BATTLE_MOVE_SELECT) {
      return;
    }
    this.#selectedAttackMenuOption = option;
    this.#moveMoveSelectBattleMenuCursor();
    this.#handlePlayerChooseAttack();
    this.#onPointerConfirm?.();
  }

  /**
   * Advance queued info-pane messages on click (same as pressing OK while waiting). Returns true
   * when a click was consumed advancing a message, so the scene can know not to treat it as a menu
   * select. Used for the PRE_BATTLE_INFO / post-attack message gates.
   * @returns {boolean}
   */
  handlePointerConfirmMessage() {
    if (this.#waitingForPlayerInput) {
      this.handlePlayerInput('OK');
      return true;
    }
    return false;
  }

  /** @type {number | undefined} */
  get selectedAttack() {
    if (this.#activeBattleMenu === ACTIVE_BATTLE_MENU.BATTLE_MOVE_SELECT) {
      return this.#selectedAttackIndex;
    }
    return undefined;
  }

  /** @type {boolean} */
  get wasItemUsed() {
    return this.#wasItemUsed;
  }

  /** @type {import('../../../types/typedef.js').Item | undefined} */
  get itemUsed() {
    return this.#usedItem;
  }

  /** @type {boolean} */
  get isAttemptingToFlee() {
    return this.#fleeAttempt;
  }

  /** @type {boolean} */
  get isAttemptingToSwitchMonsters() {
    return this.#switchMonsterAttempt;
  }

  /**
   * A compact snapshot of the menu's interactive state. The battle scene's wedge breaker compares it
   * across a confirm: if a press changes NOTHING here (and the state machine stays in PLAYER_INPUT),
   * the menu is ignoring input → after a few such no-op presses it force-resets.
   * @type {string}
   */
  get menuStateSignature() {
    return `${this.#activeBattleMenu}|${this.#selectedAttackMenuOption}|${this.#selectedAttackIndex}|${this.#waitingForPlayerInput}|${this.#queuedMessageAnimationPlaying}|${this.#infoPanelMessagesQueue.length}`;
  }

  /**
   * Hard reset to a clean main FIGHT menu — clears any wedged "waiting for input" / queued messages
   * and hides the move sub-menu. Last-resort un-wedge so a desynced menu can never permanently trap
   * the player at PLAYER_INPUT (which the battle watchdog treats as a healthy idle state).
   * @returns {void}
   */
  forceResetToMainMenu() {
    this.#waitingForPlayerInput = false;
    this.#queuedMessageAnimationPlaying = false;
    this.#infoPanelMessagesQueue = [];
    this.hideInputCursor();
    this.hideMonsterAttackSubMenu();
    this.showMainBattleMenu();
  }

  /**
   * Trigger to update the attack names after a monster has changed in the battle scene.
   * @returns {void}
   */
  updateMonsterAttackSubMenu() {
    this.#moveSelectionSubBattleMenuPhaserContainerGameObject.getAll().forEach((gameObject) => {
      if (gameObject.type === 'text') {
        /** @type {Phaser.GameObjects.Text} */
        (gameObject).setText('-');
      }
    });
    this.#activePlayerMonster.attacks.forEach((attack, index) => {
      /** @type {Phaser.GameObjects.Text} */
      (this.#moveSelectionSubBattleMenuPhaserContainerGameObject.getAt(index)).setText(
        `${attack.name}  ${Math.round((attack.power ?? 1) * 100)}`,
      );
    });
  }

  /**
   * @returns {void}
   */
  showMainBattleMenu() {
    this.#activeBattleMenu = ACTIVE_BATTLE_MENU.BATTLE_MAIN;
    this.#battleTextGameObjectLine1.setText('what should');
    this.#mainBattleMenuPhaserContainerGameObject.setAlpha(1);
    this.#battleTextGameObjectLine1.setAlpha(1);
    this.#battleTextGameObjectLine2.setAlpha(1);

    this.#selectedBattleMenuOption = BATTLE_MENU_OPTIONS.FIGHT;
    this.#mainBattleMenuCursorPhaserImageGameObject.setPosition(BATTLE_MENU_CURSOR_POS.x, BATTLE_MENU_CURSOR_POS.y);
    this.#selectedAttackIndex = undefined;
    this.#wasItemUsed = false;
    this.#fleeAttempt = false;
    this.#switchMonsterAttempt = false;
    this.#usedItem = undefined;
  }

  /**
   * @returns {void}
   */
  hideMainBattleMenu() {
    this.#mainBattleMenuPhaserContainerGameObject.setAlpha(0);
    this.#battleTextGameObjectLine1.setAlpha(0);
    this.#battleTextGameObjectLine2.setAlpha(0);
  }

  /**
   * @returns {void}
   */
  showMonsterAttackSubMenu() {
    this.#activeBattleMenu = ACTIVE_BATTLE_MENU.BATTLE_MOVE_SELECT;
    this.#moveSelectionSubBattleMenuPhaserContainerGameObject.setAlpha(1);
  }

  /**
   * @returns {void}
   */
  hideMonsterAttackSubMenu() {
    this.#activeBattleMenu = ACTIVE_BATTLE_MENU.BATTLE_MAIN;
    this.#moveSelectionSubBattleMenuPhaserContainerGameObject.setAlpha(0);
  }

  /**
   * @returns {void}
   */
  playInputCursorAnimation() {
    this.#userInputCursorPhaserImageGameObject.setPosition(
      this.#battleTextGameObjectLine1.displayWidth + this.#userInputCursorPhaserImageGameObject.displayWidth * 2.7,
      this.#userInputCursorPhaserImageGameObject.y
    );
    this.#userInputCursorPhaserImageGameObject.setAlpha(1);
    this.#userInputCursorPhaserTween.restart();
    // Players didn't know a message had to be advanced and thought the battle was frozen — show a
    // blinking "▸ SPACE / TAP" hint whenever the bobbing input cursor is up (i.e. a message gate is
    // waiting for the player to advance). Tied to the exact same show/hide lifecycle as the cursor,
    // so it is only ever visible while waiting to advance a message (never during the action/move menu).
    this.#continueHintGameObject.setAlpha(1);
    this.#continueHintTween.restart();
  }

  /**
   * @returns {void}
   */
  hideInputCursor() {
    this.#userInputCursorPhaserImageGameObject.setAlpha(0);
    this.#userInputCursorPhaserTween.pause();
    this.#continueHintGameObject.setAlpha(0);
    this.#continueHintTween.pause();
  }

  /**
   * @param {import('../../../common/direction.js').Direction|'OK'|'CANCEL'} input
   * @returns {void}
   */
  handlePlayerInput(input) {
    if (this.#queuedMessageAnimationPlaying && input === 'OK') {
      // Still scrolling → reveal the whole line instantly (tap to skip the typewriter). The next OK
      // then advances the queue.
      this.#queuedTextAnim?.skip();
      return;
    }

    if (this.#waitingForPlayerInput && (input === 'CANCEL' || input === 'OK')) {
      this.#updateInfoPaneWithMessage();
      return;
    }

    if (input === 'CANCEL') {
      this.#switchToMainBattleMenu();
      return;
    }
    if (input === 'OK') {
      if (this.#activeBattleMenu === ACTIVE_BATTLE_MENU.BATTLE_MAIN) {
        this.#handlePlayerChooseMainBattleOption();
        return;
      }
      if (this.#activeBattleMenu === ACTIVE_BATTLE_MENU.BATTLE_MOVE_SELECT) {
        this.#handlePlayerChooseAttack();
        return;
      }
      return;
    }
    this.#updateSelectedBattleMenuOptionFromInput(input);
    this.#updateSelectedMoveMenuOptionFromInput(input);
    this.#moveMainBattleMenuCursor();
    this.#moveMoveSelectBattleMenuCursor();
  }

  /**
   * @param {string} message
   * @param {() => void} [callback]
   * @returns {void}
   */
  updateInfoPaneMessageNoInputRequired(message, callback) {
    this.#battleTextGameObjectLine1.setText('').setAlpha(1);
    // Clear the move sub-menu so its labels never bleed THROUGH the dialog text (both live in the
    // same bottom-left strip) — the "word collision" players reported on catch / attack lines.
    this.#moveSelectionSubBattleMenuPhaserContainerGameObject.setAlpha(0);

    if (this.#skipAnimations) {
      this.#battleTextGameObjectLine1.setText(message);
      this.#waitingForPlayerInput = false;
      if (callback) {
        callback();
      }
      return;
    }

    animateText(this.#scene, this.#battleTextGameObjectLine1, message, {
      delay: dataManager.getAnimatedTextSpeed(),
      callback: () => {
        this.#waitingForPlayerInput = false;
        if (callback) {
          callback();
        }
      },
    });
  }

  /**
   * @param {string[]} messages
   * @param {() => void} [callback]
   * @returns {void}
   */
  updateInfoPaneMessagesAndWaitForInput(messages, callback) {
    this.#infoPanelMessagesQueue.push({
      messages,
      callback,
      isProcessing: false,
    });

    if (this.#infoPanelMessagesQueue.length === 1) {
      this.#updateInfoPaneWithMessage();
    }
  }

  /**
   * @returns {void}
   */
  #updateInfoPaneWithMessage() {
    this.#waitingForPlayerInput = false;
    this.#battleTextGameObjectLine1.setText('').setAlpha(1);
    this.#moveSelectionSubBattleMenuPhaserContainerGameObject.setAlpha(0);
    this.hideInputCursor();

    if (this.#infoPanelMessagesQueue.length === 0) {
      return;
    }

    const currentMessage = this.#infoPanelMessagesQueue[0];
    if (!currentMessage.isProcessing) {
      currentMessage.isProcessing = true;
    }

    // check if all messages have been displayed from the queue and call the callback
    if (currentMessage.messages.length === 0) {
      if (currentMessage.callback) {
        currentMessage.callback();
      }
      this.#infoPanelMessagesQueue.shift();

      if (this.#infoPanelMessagesQueue.length > 0) {
        this.#updateInfoPaneWithMessage();
      }
      return;
    }

    // get first message from queue and animate message
    const messageToDisplay = currentMessage.messages.shift();

    if (this.#skipAnimations) {
      this.#battleTextGameObjectLine1.setText(messageToDisplay);
      this.#queuedMessageAnimationPlaying = false;
      this.#waitingForPlayerInput = true;
      this.playInputCursorAnimation();
      return;
    }

    this.#queuedMessageAnimationPlaying = true;
    this.#queuedTextAnim = animateText(this.#scene, this.#battleTextGameObjectLine1, messageToDisplay, {
      delay: dataManager.getAnimatedTextSpeed(),
      callback: () => {
        this.playInputCursorAnimation();
        this.#waitingForPlayerInput = true;
        this.#queuedMessageAnimationPlaying = false;
      },
    });
  }

  /**
   * @returns {void}
   */
  #createMainBattleMenu() {
    this.#battleTextGameObjectLine1 = this.#scene.add.text(20, 468, 'what should', {
      ...BATTLE_UI_TEXT_STYLE,
      ...{
        wordWrap: {
          width: BATTLE_DESIGN_WIDTH - 55,
        },
      },
    });
    this.#battleTextGameObjectLine2 = this.#scene.add.text(
      20,
      512,
      `${this.#activePlayerMonster.name} do next?`,
      BATTLE_UI_TEXT_STYLE
    );

    this.#mainBattleMenuCursorPhaserImageGameObject = this.#scene.add
      .image(BATTLE_MENU_CURSOR_POS.x, BATTLE_MENU_CURSOR_POS.y, UI_ASSET_KEYS.CURSOR, 0)
      .setOrigin(0.5)
      .setScale(2.5);

    this.#mainBattleMenuPhaserContainerGameObject = this.#scene.add.container(520, 448, [
      this.#createMainInfoSubPane(),
      this.#scene.add.text(55, 22, BATTLE_MENU_OPTIONS.FIGHT, BATTLE_UI_TEXT_STYLE),
      this.#scene.add.text(240, 22, BATTLE_MENU_OPTIONS.SWITCH, BATTLE_UI_TEXT_STYLE),
      this.#scene.add.text(55, 70, BATTLE_MENU_OPTIONS.ITEM, BATTLE_UI_TEXT_STYLE),
      this.#scene.add.text(240, 70, BATTLE_MENU_OPTIONS.FLEE, BATTLE_UI_TEXT_STYLE),
      this.#mainBattleMenuCursorPhaserImageGameObject,
    ]);

    this.hideMainBattleMenu();
  }

  /**
   * @returns {void}
   */
  #createMonsterAttackSubMenu() {
    this.#attackBattleMenuCursorPhaserImageGameObject = this.#scene.add
      .image(ATTACK_MENU_CURSOR_POS.x, ATTACK_MENU_CURSOR_POS.y, UI_ASSET_KEYS.CURSOR, 0)
      .setOrigin(0.5)
      .setScale(2.5);

    /** @type {string[]} */
    const attackNames = [];
    for (let i = 0; i < 4; i += 1) {
      const a = this.#activePlayerMonster.attacks[i];
      attackNames.push(a ? `${a.name}  ${Math.round((a.power ?? 1) * 100)}` : '-');
    }

    // Smaller font for move names: the 4-move sets include longer names (Flame Claw, Boulder Smash…)
    // plus the appended power rating, so 18px keeps "Boulder Smash  150" on one line within its column.
    const moveTextStyle = { ...BATTLE_UI_TEXT_STYLE, fontSize: '18px' };
    this.#moveSelectionSubBattleMenuPhaserContainerGameObject = this.#scene.add.container(0, 448, [
      this.#scene.add.text(55, 26, attackNames[0], moveTextStyle),
      this.#scene.add.text(245, 26, attackNames[1], moveTextStyle),
      this.#scene.add.text(55, 72, attackNames[2], moveTextStyle),
      this.#scene.add.text(245, 72, attackNames[3], moveTextStyle),
      this.#attackBattleMenuCursorPhaserImageGameObject,
    ]);
    this.hideMonsterAttackSubMenu();
  }

  /**
   * @returns {void}
   */
  #createMainInfoPane() {
    const padding = 4;
    const rectHeight = 124;

    this.#scene.add
      .rectangle(
        padding,
        BATTLE_DESIGN_HEIGHT - rectHeight - padding,
        BATTLE_DESIGN_WIDTH - padding * 2,
        rectHeight,
        0xede4f3,
        1
      )
      .setOrigin(0)
      .setStrokeStyle(8, 0xe4434a, 1);
  }

  /**
   * @returns {Phaser.GameObjects.Rectangle}
   */
  #createMainInfoSubPane() {
    const rectWidth = 500;
    const rectHeight = 124;

    return this.#scene.add
      .rectangle(0, 0, rectWidth, rectHeight, 0xede4f3, 1)
      .setOrigin(0)
      .setStrokeStyle(8, 0x905ac2, 1);
  }

  /**
   * @param {import('../../../common/direction.js').Direction} direction
   */
  #updateSelectedBattleMenuOptionFromInput(direction) {
    if (this.#activeBattleMenu !== ACTIVE_BATTLE_MENU.BATTLE_MAIN) {
      return;
    }

    if (this.#selectedBattleMenuOption === BATTLE_MENU_OPTIONS.FIGHT) {
      switch (direction) {
        case DIRECTION.RIGHT:
          this.#selectedBattleMenuOption = BATTLE_MENU_OPTIONS.SWITCH;
          return;
        case DIRECTION.DOWN:
          this.#selectedBattleMenuOption = BATTLE_MENU_OPTIONS.ITEM;
          return;
        case DIRECTION.LEFT:
        case DIRECTION.UP:
        case DIRECTION.NONE:
          return;
        default:
          exhaustiveGuard(direction);
      }
      return;
    }

    if (this.#selectedBattleMenuOption === BATTLE_MENU_OPTIONS.SWITCH) {
      switch (direction) {
        case DIRECTION.LEFT:
          this.#selectedBattleMenuOption = BATTLE_MENU_OPTIONS.FIGHT;
          return;
        case DIRECTION.DOWN:
          this.#selectedBattleMenuOption = BATTLE_MENU_OPTIONS.FLEE;
          return;
        case DIRECTION.RIGHT:
        case DIRECTION.UP:
        case DIRECTION.NONE:
          return;
        default:
          exhaustiveGuard(direction);
      }
      return;
    }

    if (this.#selectedBattleMenuOption === BATTLE_MENU_OPTIONS.ITEM) {
      switch (direction) {
        case DIRECTION.RIGHT:
          this.#selectedBattleMenuOption = BATTLE_MENU_OPTIONS.FLEE;
          return;
        case DIRECTION.UP:
          this.#selectedBattleMenuOption = BATTLE_MENU_OPTIONS.FIGHT;
          return;
        case DIRECTION.LEFT:
        case DIRECTION.DOWN:
        case DIRECTION.NONE:
          return;
        default:
          exhaustiveGuard(direction);
      }
      return;
    }

    if (this.#selectedBattleMenuOption === BATTLE_MENU_OPTIONS.FLEE) {
      switch (direction) {
        case DIRECTION.LEFT:
          this.#selectedBattleMenuOption = BATTLE_MENU_OPTIONS.ITEM;
          return;
        case DIRECTION.UP:
          this.#selectedBattleMenuOption = BATTLE_MENU_OPTIONS.SWITCH;
          return;
        case DIRECTION.RIGHT:
        case DIRECTION.DOWN:
        case DIRECTION.NONE:
          return;
        default:
          exhaustiveGuard(direction);
      }
      return;
    }

    exhaustiveGuard(this.#selectedBattleMenuOption);
  }

  /**
   * @returns {void}
   */
  #moveMainBattleMenuCursor() {
    if (this.#activeBattleMenu !== ACTIVE_BATTLE_MENU.BATTLE_MAIN) {
      return;
    }

    switch (this.#selectedBattleMenuOption) {
      case BATTLE_MENU_OPTIONS.FIGHT:
        this.#mainBattleMenuCursorPhaserImageGameObject.setPosition(BATTLE_MENU_CURSOR_POS.x, BATTLE_MENU_CURSOR_POS.y);
        return;
      case BATTLE_MENU_OPTIONS.SWITCH:
        this.#mainBattleMenuCursorPhaserImageGameObject.setPosition(228, BATTLE_MENU_CURSOR_POS.y);
        return;
      case BATTLE_MENU_OPTIONS.ITEM:
        this.#mainBattleMenuCursorPhaserImageGameObject.setPosition(BATTLE_MENU_CURSOR_POS.x, 86);
        return;
      case BATTLE_MENU_OPTIONS.FLEE:
        this.#mainBattleMenuCursorPhaserImageGameObject.setPosition(228, 86);
        return;
      default:
        exhaustiveGuard(this.#selectedBattleMenuOption);
    }
  }

  /**
   * @param {import('../../../common/direction.js').Direction} direction
   */
  #updateSelectedMoveMenuOptionFromInput(direction) {
    if (this.#activeBattleMenu !== ACTIVE_BATTLE_MENU.BATTLE_MOVE_SELECT) {
      return;
    }

    if (this.#selectedAttackMenuOption === ATTACK_MOVE_OPTIONS.MOVE_1) {
      switch (direction) {
        case DIRECTION.RIGHT:
          this.#selectedAttackMenuOption = ATTACK_MOVE_OPTIONS.MOVE_2;
          return;
        case DIRECTION.DOWN:
          this.#selectedAttackMenuOption = ATTACK_MOVE_OPTIONS.MOVE_3;
          return;
        case DIRECTION.LEFT:
        case DIRECTION.UP:
        case DIRECTION.NONE:
          return;
        default:
          exhaustiveGuard(direction);
      }
      return;
    }

    if (this.#selectedAttackMenuOption === ATTACK_MOVE_OPTIONS.MOVE_2) {
      switch (direction) {
        case DIRECTION.LEFT:
          this.#selectedAttackMenuOption = ATTACK_MOVE_OPTIONS.MOVE_1;
          return;
        case DIRECTION.DOWN:
          this.#selectedAttackMenuOption = ATTACK_MOVE_OPTIONS.MOVE_4;
          return;
        case DIRECTION.RIGHT:
        case DIRECTION.UP:
        case DIRECTION.NONE:
          return;
        default:
          exhaustiveGuard(direction);
      }
      return;
    }

    if (this.#selectedAttackMenuOption === ATTACK_MOVE_OPTIONS.MOVE_3) {
      switch (direction) {
        case DIRECTION.RIGHT:
          this.#selectedAttackMenuOption = ATTACK_MOVE_OPTIONS.MOVE_4;
          return;
        case DIRECTION.UP:
          this.#selectedAttackMenuOption = ATTACK_MOVE_OPTIONS.MOVE_1;
          return;
        case DIRECTION.LEFT:
        case DIRECTION.DOWN:
        case DIRECTION.NONE:
          return;
        default:
          exhaustiveGuard(direction);
      }
      return;
    }

    if (this.#selectedAttackMenuOption === ATTACK_MOVE_OPTIONS.MOVE_4) {
      switch (direction) {
        case DIRECTION.LEFT:
          this.#selectedAttackMenuOption = ATTACK_MOVE_OPTIONS.MOVE_3;
          return;
        case DIRECTION.UP:
          this.#selectedAttackMenuOption = ATTACK_MOVE_OPTIONS.MOVE_2;
          return;
        case DIRECTION.RIGHT:
        case DIRECTION.DOWN:
        case DIRECTION.NONE:
          return;
        default:
          exhaustiveGuard(direction);
      }
      return;
    }

    exhaustiveGuard(this.#selectedAttackMenuOption);
  }

  /**
   * @returns {void}
   */
  #moveMoveSelectBattleMenuCursor() {
    if (this.#activeBattleMenu !== ACTIVE_BATTLE_MENU.BATTLE_MOVE_SELECT) {
      return;
    }

    switch (this.#selectedAttackMenuOption) {
      case ATTACK_MOVE_OPTIONS.MOVE_1:
        this.#attackBattleMenuCursorPhaserImageGameObject.setPosition(
          ATTACK_MENU_CURSOR_POS.x,
          ATTACK_MENU_CURSOR_POS.y
        );
        return;
      case ATTACK_MOVE_OPTIONS.MOVE_2:
        this.#attackBattleMenuCursorPhaserImageGameObject.setPosition(228, ATTACK_MENU_CURSOR_POS.y);
        return;
      case ATTACK_MOVE_OPTIONS.MOVE_3:
        this.#attackBattleMenuCursorPhaserImageGameObject.setPosition(ATTACK_MENU_CURSOR_POS.x, 86);
        return;
      case ATTACK_MOVE_OPTIONS.MOVE_4:
        this.#attackBattleMenuCursorPhaserImageGameObject.setPosition(228, 86);
        return;
      default:
        exhaustiveGuard(this.#selectedAttackMenuOption);
    }
  }

  /**
   * @returns {void}
   */
  #switchToMainBattleMenu() {
    this.#waitingForPlayerInput = false;
    this.hideInputCursor();
    this.hideMonsterAttackSubMenu();
    this.showMainBattleMenu();
  }

  /**
   * @returns {void}
   */
  #handlePlayerChooseMainBattleOption() {
    this.hideMainBattleMenu();

    if (this.#selectedBattleMenuOption === BATTLE_MENU_OPTIONS.FIGHT) {
      this.showMonsterAttackSubMenu();
      return;
    }

    if (this.#selectedBattleMenuOption === BATTLE_MENU_OPTIONS.ITEM) {
      this.#activeBattleMenu = ACTIVE_BATTLE_MENU.BATTLE_ITEM;

      // pause this scene and launch the inventory scene
      /** @type {import('../../../scenes/inventory-scene.js').InventorySceneData} */
      const sceneDataToPass = {
        previousSceneName: SCENE_KEYS.BATTLE_SCENE,
        itemCategoriesThatCannotBeUsed: this.#isTrainerBattle ? [ITEM_CATEGORY.CAPTURE] : [],
      };
      this.#scene.scene.launch(SCENE_KEYS.INVENTORY_SCENE, sceneDataToPass);
      this.#scene.scene.pause(SCENE_KEYS.BATTLE_SCENE);
      return;
    }

    if (this.#selectedBattleMenuOption === BATTLE_MENU_OPTIONS.SWITCH) {
      this.#activeBattleMenu = ACTIVE_BATTLE_MENU.BATTLE_SWITCH;
      this.#switchMonsterAttempt = true;
      return;
    }

    if (this.#selectedBattleMenuOption === BATTLE_MENU_OPTIONS.FLEE) {
      if (this.#isTrainerBattle) {
        this.updateInfoPaneMessagesAndWaitForInput(["You can't flee at this time!"], () => {
          this.showMainBattleMenu();
        });
        return;
      }

      this.#activeBattleMenu = ACTIVE_BATTLE_MENU.BATTLE_FLEE;
      this.#fleeAttempt = true;
      return;
    }

    exhaustiveGuard(this.#selectedBattleMenuOption);
  }

  /**
   * @returns {void}
   */
  #handlePlayerChooseAttack() {
    let selectedMoveIndex = 0;
    switch (this.#selectedAttackMenuOption) {
      case ATTACK_MOVE_OPTIONS.MOVE_1:
        selectedMoveIndex = 0;
        break;
      case ATTACK_MOVE_OPTIONS.MOVE_2:
        selectedMoveIndex = 1;
        break;
      case ATTACK_MOVE_OPTIONS.MOVE_3:
        selectedMoveIndex = 2;
        break;
      case ATTACK_MOVE_OPTIONS.MOVE_4:
        selectedMoveIndex = 3;
        break;
      default:
        exhaustiveGuard(this.#selectedAttackMenuOption);
    }

    this.#selectedAttackIndex = selectedMoveIndex;
  }

  /**
   * @returns {void}
   */
  #createPlayerInputCursor() {
    this.#userInputCursorPhaserImageGameObject = this.#scene.add.image(0, 0, UI_ASSET_KEYS.CURSOR);
    this.#userInputCursorPhaserImageGameObject.setAngle(90).setScale(2.5, 1.25);
    this.#userInputCursorPhaserImageGameObject.setAlpha(0);

    this.#userInputCursorPhaserTween = this.#scene.add.tween({
      delay: 0,
      duration: 500,
      repeat: -1,
      y: {
        from: PLAYER_INPUT_CURSOR_POS.y,
        start: PLAYER_INPUT_CURSOR_POS.y,
        to: PLAYER_INPUT_CURSOR_POS.y + 6,
      },
      targets: this.#userInputCursorPhaserImageGameObject,
    });
    this.#userInputCursorPhaserTween.pause();

    // "press to continue" affordance, pinned to the bottom-right of the message pane (left of the
    // action menu sub-pane at x=520). Blinks so it reads as an interactive prompt. Shown/hidden in
    // lockstep with the input cursor (see playInputCursorAnimation / hideInputCursor), so it only
    // appears while a battle message is waiting to be advanced.
    this.#continueHintGameObject = this.#scene.add
      .text(505, 555, '▸ SPACE / TAP', {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        color: '#905ac2',
        fontSize: '18px',
      })
      .setOrigin(1, 0.5)
      .setAlpha(0);
    this.#continueHintTween = this.#scene.add.tween({
      delay: 0,
      duration: 500,
      repeat: -1,
      yoyo: true,
      alpha: {
        from: 1,
        start: 1,
        to: 0.25,
      },
      targets: this.#continueHintGameObject,
    });
    this.#continueHintTween.pause();
  }

  /**
   * @param {Phaser.Scenes.Systems} sys
   * @param {import('../../../scenes/battle-scene.js').BattleSceneWasResumedData} data
   * @returns {void}
   */
  #handleSceneResume(sys, data) {
    console.log(
      `[${BattleMenu.name}:handleSceneResume] scene has been resumed, data provided: ${JSON.stringify(data)}`
    );

    if (data && data.wasMonsterSelected) {
      // do nothing since new active monster was chosen to switch to
      return;
    }

    if (!data || !data.wasItemUsed) {
      this.#switchToMainBattleMenu();
      return;
    }

    this.#wasItemUsed = true;
    this.#usedItem = data.item;
    this.updateInfoPaneMessagesAndWaitForInput([`You used the following item: ${data.item.name}`]);
  }
}
